'use client';

import './modal.scss';

import type { DialogProps as RdxRootProps } from '@radix-ui/react-dialog';
import {
  Close as RdxClose,
  Content as RdxContent,
  Overlay as RdxOverlay,
  Portal as RdxPortal,
  Root as RdxRoot,
  Trigger as RdxTrigger,
} from '@radix-ui/react-dialog';
import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef } from 'react';
import { type JSX, createContext, use, useEffect, useRef } from 'react';

import { CloseButton } from '../../atoms/close-button/close-button.tsx';
import { cn } from '../../utils/helpers.ts';

/**
 * Shared modal state provided by the Root to every part (content, close button).
 * `dismissible` governs USER-initiated close (Esc, outside click, X). It does
 * NOT affect programmatic close via `open`/`onOpenChange`.
 */
interface ModalContextValue {
  controlledTriggerRef: React.RefObject<HTMLElement | null>;
  dismissible: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

/**
 * Controlled triggers are used outside the Modal context, so this is the smoothest
 * way for open Modals to know which trigger opened it. Whenever a trigger is clicked,
 * it adds this attribute, the modal finds it and stores the ref, then removes it from the trigger.
 * The one catch here is an extremely unlikely scenario when a dev accidentally adds a trigger that doesn't activate a modal.
 * If that is clicked, and later the very Modal is opened programmatically without any trigger, it will pick up the previous
 * trigger and restore it focus to it when closed. We have a lint warning devs to ensure a controlled trigger is always controlling
 * the open state of a Modal (which is the very purpose of the controlled trigger).
 */
const controlledTriggerAttribute = 'data-modal-controlled-trigger';

const useModalContext = () => {
  const context = use(ModalContext);

  if (!context) {
    throw new Error('Modal components must be rendered within Modal');
  }

  return context;
};

export interface ModalProps extends ComponentPropsWithoutRef<'div'> {
  defaultOpen?: RdxRootProps['defaultOpen'];
  dismissible?: boolean;
  modal?: RdxRootProps['modal'];
  onClose?: () => void;
  onOpen?: () => void;
  onOpenChange?: RdxRootProps['onOpenChange'];
  open?: RdxRootProps['open'];
}

export const Modal: React.FC<ModalProps> = ({
  children,
  dismissible = true,
  onClose,
  onOpen,
  onOpenChange,
  open,
  ...rdxProps
}) => {
  const controlledTriggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const controlledTrigger = globalThis.document.querySelector<HTMLElement>(
      `[${controlledTriggerAttribute}]`,
    );
    if (!controlledTrigger) {
      return;
    }

    controlledTriggerRef.current = controlledTrigger;
    controlledTrigger.removeAttribute(controlledTriggerAttribute);
  }, [open]);

  return (
    <ModalContext value={{ controlledTriggerRef, dismissible }}>
      <RdxRoot
        open={open}
        onOpenChange={(nextOpen) => {
          if (onOpenChange) {
            onOpenChange(nextOpen);
          }
          if (onOpen && nextOpen) {
            onOpen();
          } else if (onClose && !nextOpen) {
            onClose();
          }
        }}
        {...rdxProps}
      >
        {children}
      </RdxRoot>
    </ModalContext>
  );
};

/**
 * Modal content.
 */
export interface ModalContentProps extends ComponentPropsWithoutRef<
  typeof RdxContent
> {
  className?: string;
}

export const ModalContent: React.FC<
  ModalContentProps & { ref?: React.Ref<HTMLDivElement> }
> = ({
  style,
  className,
  ref,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...props
}) => {
  const { controlledTriggerRef, dismissible } = useModalContext();

  const handleCloseAutoFocus: ModalContentProps['onCloseAutoFocus'] = (
    event,
  ) => {
    onCloseAutoFocus?.(event);

    const controlledTrigger = controlledTriggerRef.current;
    controlledTriggerRef.current = null;

    if (event.defaultPrevented) {
      return;
    }

    if (!controlledTrigger?.isConnected) {
      return;
    }

    controlledTrigger.focus();

    if (controlledTrigger.ownerDocument.activeElement === controlledTrigger) {
      event.preventDefault();
    }
  };

  const handleEscapeKeyDown: ModalContentProps['onEscapeKeyDown'] = (event) => {
    if (!dismissible) {
      event.preventDefault();
    }
    onEscapeKeyDown?.(event);
  };

  const handlePointerDownOutside: ModalContentProps['onPointerDownOutside'] = (
    event,
  ) => {
    if (!dismissible) {
      event.preventDefault();
    }
    onPointerDownOutside?.(event);
  };

  return (
    <RdxPortal>
      <RdxOverlay className="Modal__overlay" />
      <RdxContent
        ref={ref}
        aria-describedby={undefined}
        className={cn('Modal__content', {
          [`${className}`]: !!className,
        })}
        style={{
          ...style,
        }}
        onCloseAutoFocus={handleCloseAutoFocus}
        onEscapeKeyDown={handleEscapeKeyDown}
        onPointerDownOutside={handlePointerDownOutside}
        {...props}
        tabIndex={undefined}
      >
        {props.children}
      </RdxContent>
    </RdxPortal>
  );
};

/**
 * Modal trigger.
 */
export type ModalTriggerProps = {
  children: JSX.Element;
};
export const ModalTrigger: React.FC<ModalTriggerProps> = ({ children }) => {
  return <RdxTrigger asChild>{children}</RdxTrigger>;
};

export type ModalControlledTriggerProps = {
  children: JSX.Element;
};

export const ModalControlledTrigger: React.FC<ModalControlledTriggerProps> = ({
  children,
}) => {
  return (
    <Slot
      onClick={(event) => {
        const { ownerDocument } = event.currentTarget;
        for (const controlledTrigger of ownerDocument.querySelectorAll(
          `[${controlledTriggerAttribute}]`,
        )) {
          controlledTrigger.removeAttribute(controlledTriggerAttribute);
        }
        event.currentTarget.setAttribute(controlledTriggerAttribute, '');
      }}
    >
      {children}
    </Slot>
  );
};

/**
 * Modal close.
 */
export const ModalClose = RdxClose;

/**
 * Modal title.
 */

/**
 * Modal close button.
 */
export const ModalCloseButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    className?: string;
    name?: string;
  }
> = ({ className, name, disabled, ...props }) => {
  const { dismissible } = useModalContext();

  return (
    <ModalClose asChild>
      <CloseButton
        className={cn('Modal__close', {
          [`${className}`]: !!className,
        })}
        aria-label={`Close ${name}`}
        disabled={disabled || !dismissible}
        {...props}
      />
    </ModalClose>
  );
};

export { Title as ModalTitle } from '@radix-ui/react-dialog';
